import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

// Lightweight spotlight tour (no deps). steps: [{ target: css selector, title, body }]
export default function WelcomeTour({ steps, onClose, zh }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "nearest", inline: "nearest" });
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 250);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, [measure]);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(false); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  const last = i === steps.length - 1;
  const next = () => (last ? onClose(true) : setI((v) => Math.min(steps.length - 1, v + 1)));
  const prev = () => setI((v) => Math.max(0, v - 1));
  if (!step) return null;

  const pad = 8;
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(340, vw - 24);
  let cardStyle;
  if (rect) {
    const below = rect.top + rect.height + pad + 12;
    const placeBelow = below + 200 < vh;
    let left = Math.min(Math.max(12, rect.left + rect.width / 2 - cardW / 2), vw - cardW - 12);
    cardStyle = placeBelow ? { top: below, left } : { bottom: vh - rect.top + pad + 12, left };
  } else {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  }

  return (
    <div className="fixed inset-0 z-[300]" data-testid="welcome-tour" data-step={i}>
      {rect ? (
        <div className="absolute rounded-2xl ring-2 ring-brand-400 transition-all duration-300 pointer-events-none"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(10,14,30,0.62)" }} />
      ) : <div className="absolute inset-0 bg-slate-950/60" />}
      <div className="absolute inset-0" onClick={() => onClose(false)} />
      <div className="absolute rounded-2xl bg-white dark:bg-[#151a2e] border border-slate-200/80 dark:border-[#2c3556] shadow-lift p-5 fade-up" style={{ ...cardStyle, width: cardW }} data-testid="tour-card" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onClose(false)} data-testid="tour-skip" className="absolute right-3 top-3 h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10" aria-label="skip"><X className="h-4 w-4" /></button>
        <div className="flex items-center gap-2 text-brand-600 text-[11px] font-bold uppercase tracking-wider mb-2"><Sparkles className="h-3.5 w-3.5" />{zh ? `新界面引导 ${i + 1}/${steps.length}` : `Tour ${i + 1}/${steps.length}`}</div>
        <h3 className="text-base font-extrabold text-slate-900 dark:text-slate-100 font-display" data-testid="tour-title">{step.title}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed" data-testid="tour-body">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1.5">
            {steps.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-brand-500" : "w-1.5 bg-slate-200 dark:bg-slate-600"}`} />)}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && <button onClick={prev} data-testid="tour-prev" className="btn-soft !py-1.5 !px-3 text-xs"><ChevronLeft className="h-3.5 w-3.5" />{zh ? "上一步" : "Back"}</button>}
            <button onClick={next} data-testid={last ? "tour-done" : "tour-next"} className="btn-brand !py-1.5 !px-3.5 text-xs">
              {last ? (zh ? "开始使用" : "Get started") : (zh ? "下一步" : "Next")}{!last && <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
